<template>
    <div>
        <span class="text-lg">
            Trạng thái điều độ của chặng hàng
        </span>
        <div class="mt-5 grid grid-cols-1 sm:grid-cols-4 gap-x-4 gap-y-8">
            <analytics-card
                :text="$t('labels.officialVehicle')"
                :value="$formatters.formatCurrency(getValueReport(4))"
                color="success"
                increase
                @click="getItems(4)" />
            <analytics-card
                :text="$t('labels.expectedVehicles')"
                :value="$formatters.formatCurrency(getValueReport(3))"
                color="accent"
                increase
                @click="getItems(3)" />
            <analytics-card
                :text="$t('labels.awaitingVehicleDispatch')"
                :value="$formatters.formatCurrency(getValueReport(2))"
                color="accent"
                increase
                @click="getItems(2)" />
            <analytics-card
                :text="$t('labels.waitingConfirm')"
                :value="$formatters.formatCurrency(getValueReport(1))"
                color="error"
                increase
                @click="getItems(1)" />
        </div>
        <div class="mt-5 grid grid-cols-1 sm:grid-cols-4 gap-x-4 gap-y-8">
            <div class="sm:col-start-4">
                <analytics-card
                    :text="$t('labels.waitingForVehicleDispatch')"
                    :value="$formatters.formatCurrency(getValueReport(7))"
                    color="error"
                    increase
                    @click="getItems(7)" />
            </div>
        </div>
    </div>
    <analytics-modal
        v-model:visible="visible"
        :headers="headers"
        :loading="loading"
        :items="items" />
</template>
<script lang="ts" setup>
import AnalyticsCard from '../analytics-card.vue'
import AnalyticsModal from '../analytics-modal.vue'
import Status from '~/components/status.vue'

import { $api, $lodash } from '#imports'
const { $formatters } = useNuxtApp()

const dataAnalytics = ref<
  Array<{
    status: number
    total: number
  }>
>([])

onMounted(async () => {
  const api = $url.dashboard.directionOfFleet.getCargoSegmentStatus
  const data: any = await $api(api)
  const resData: any = $lodash.get(data, 'data.value')
  if (resData?.success) {
    dataAnalytics.value = resData.data
  }
})

const getValueReport = (value: number) => {
  const item = dataAnalytics.value.find((i: { status: number; total: number }) => i.status === value)
  return item?.total ?? 0
}

const items = ref<Array<any>>([])
const store = $store()

const loading = ref(false)
const visible = ref(false)

const getItems = async (status: number) => {
  try {
    loading.value = true
    visible.value = true
    const api = $url.dashboard.directionOfFleet.getDataCargoSegmentStatus
    const data: any = await $api(api, { uriParams: { STATUS: status } })
    const resData: any = $lodash.get(data, 'data.value')
    if (resData?.success) {
      items.value = resData.data
    }
    loading.value = false
  } catch (e: any) {
    loading.value = false
    visible.value = false
  }
}

const headers = ref([
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'containerNo',
    headerName: $t('labels.containerNo'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'orderCode',
    headerName: $t('labels.order'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'estimateStartTime.departureTime',
    headerName: 'Thời gian dự kiến khởi hành',
    valueGetter: (params: any) => {
      const data = params.data.startAt
      if (!data) {
        return ''
      }
      return $formatters.datetime(data)
    }
  },
  {
    headerName: 'Thời gian thực hiện chặng hàng',
    valueGetter: (params: any) => {
      const timeIn = params.data?.timeIn
      const timeOut = params.data?.timeOut
      return $formatters.datetime(timeIn) + ' - ' + $formatters.datetime(timeOut)
    }
  },
  {
    field: 'estimateStartTime.departureLocationName',
    headerName: 'Địa điểm khởi hành'
  },
  {
    field: 'journeyName',
    headerName: $t('labels.journey2'),
    valueGetter: (params: any) => {
      const stages = params.data?.stages
      const names = stages.map((item: any) => item.nodeLocationName)
      return names.join(' - ')
    }
  },
  {
    field: 'vehicleLicensePlate',
    headerName: $t('labels.vehicle'),
    valueGetter: (params: any) => {
      const vehicleLicensePlate = params.data?.vehicleLicensePlate
      const vehicleTempLicensePlate = params.data?.vehicleTempLicensePlate
      return vehicleLicensePlate || vehicleTempLicensePlate || ''
    }
  },
  {
    field: 'journeyIndex',
    headerName: $t('labels.journeyIndex')
  },
  {
    field: 'partnerName',
    headerName: $t('labels.fleetVehicle')
  },
  {
    field: 'transportation',
    sortable: true,
    headerName: $t('labels.transportation'),
    valueGetter: (params: any) => {
      const transportation = params?.data.orderData?.transportation
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'contCategory',
    headerName: $t('labels.cont'),
    valueGetter: (params: any) => {
      const contCategory = params.data?.orderData?.contCategory
      if (contCategory) {
        if (contCategory === 20) {
          return $t('labels.20f')
        }
        if (contCategory === 40) {
          return $t('labels.40f')
        }
        if (contCategory === 45) {
          return $t('labels.45f')
        }
      }
    }
  },
  {
    field: 'mergeContainer',
    headerName: $t('labels.mergeContainer'),
    valueGetter: (params: any) => {
      const mergeContainer = params?.data?.orderData?.mergeContainer
      if (mergeContainer) {
        return $t('labels.haveMergeContainer')
      }
      return $t('labels.notMergeContainer')
    }
  },
  {
    field: 'headContainer',
    headerName: $t('labels.headContainer'),
    valueGetter: (params: any) => {
      const headContainer = params?.data?.orderData?.headContainer
      if (headContainer) {
        return $t('labels.true')
      }
      return $t('labels.false')
    }
  },
  {
    field: 'commodityName',
    headerName: $t('labels.commodity'),
    valueGetter: (params: any) => {
      const commodityName = params?.data?.orderData?.commodityName
      const commodityCategory = params?.data?.orderData?.commodityCategory
      if (commodityName && commodityCategory) {
        return `${commodityName} - ${commodityCategory}`
      }
      if (commodityName) {
        return commodityName
      }
      return ''
    }
  },
  {
    field: 'weightCommodity',
    headerName: $t('labels.weightCommodityByKG1'),
    valueGetter: (params: any) => {
      return $formatters.formatNumber(params.data.orderData?.weightCommodity)
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'journeyStatus',
      label: '',
      keyStatus: 'status'
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  }
])

const listOfReports = [
  {
    status: 1,
    label: $t('labels.waitingConfirm'),
    color: 'info'
  },
  {
    status: 2,
    label: $t('labels.awaitingVehicleDispatch')
  },
  {
    status: 3,
    label: $t('labels.expectedVehicles'),
    color: 'info'
  },
  {
    status: 4,
    label: $t('labels.officialVehicle')
  },
  {
    status: 5,
    label: $t('labels.inTransit'),
    color: 'info'
  },
  {
    status: 6,
    label: $t('labels.segmentCompletion'),
    color: 'success'
  },
  {
    status: 7,
    label: $t('labels.waitingForVehicleDispatch'),
    color: 'info'
  }
]
</script>
